import { computed, Inject, Injectable, Optional, Signal, signal } from '@angular/core';
import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import {
  GalaxyFilterChipDependencies,
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
  GalaxyFilterType,
} from '@vendasta/galaxy/filter/chips';
import { Dispute, DisputeStatus, disputeStatusToApi, isDisputeStatus, PaymentService } from '@galaxy/billing';
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DisputeStatus as DisputeStatusApi } from '@vendasta/billing';
import { CUSTOMER_DATA_SERVICE, CustomerData, CustomerDataService } from '../../customer/customer-data';
import { stripReferencePrefixFromPaymentDescription } from '../../transactions/common';

interface DisputeTableFilters {
  status?: DisputeStatusApi;
}

@Injectable()
export class DisputesTableDatasource implements PaginatedAPIInterface<Dispute>, GalaxyFilterChipDependencies {
  private readonly galaxyFilters = signal<GalaxyFilterInterface[]>([]);
  private merchantId: string;

  private readonly disputesFilter: Signal<DisputeTableFilters> = computed(() => {
    const filters: DisputeTableFilters = {};

    const statusFilter = this.galaxyFilters().find((f) => f?.fieldId === 'status')?.values?.[0].string;
    if (isDisputeStatus(statusFilter)) {
      filters.status = disputeStatusToApi(statusFilter);
    } else if (!statusFilter) {
      filters.status = DisputeStatusApi.DISPUTE_STATUS_UNSET;
    }
    return filters;
  });

  private filterChanges$ = toObservable(this.disputesFilter).pipe(debounceTime(250), distinctUntilChanged());

  constructor(
    private paymentService: PaymentService,
    private translateService: TranslateService,
    @Optional() @Inject(CUSTOMER_DATA_SERVICE) private customerDataService: CustomerDataService,
  ) {}

  updateFilters(filters: GalaxyFilterInterface[]): void {
    this.galaxyFilters.set(filters);
  }

  getInitialAppliedFilters(): Promise<GalaxyFilterInterface[]> {
    return Promise.resolve(this.galaxyFilters());
  }

  setInitialAppliedFilters(filters: GalaxyFilterInterface[]) {
    this.galaxyFilters.set(filters);
  }

  public setMerchantId(merchantId: string): void {
    this.merchantId = merchantId;
  }

  get(r: PagedListRequestInterface): Observable<PagedResponseInterface<Dispute>> {
    const disputesResp$ = this.filterChanges$.pipe(
      switchMap(() => {
        return this.paymentService.listRetailDisputes(
          this.merchantId,
          r.pagingOptions.cursor ?? '',
          r.pagingOptions.pageSize ?? 25,
          this.disputesFilter().status,
        );
      }),
      map((response) => {
        const disputes = (response.results || []).map((d) => {
          d.description = stripReferencePrefixFromPaymentDescription(d.description, d.referenceType);
          return d;
        });
        return {
          data: disputes,
          pagingMetadata: {
            nextCursor: response.nextCursor,
            hasMore: response.hasMore,
          },
        };
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const customerData$: Observable<CustomerData[]> = disputesResp$.pipe(
      map((resp) => {
        return resp.data.map((d) => d?.customerId).filter((id) => !!id);
      }),
      switchMap((ids) => {
        if (!ids || ids.length === 0) {
          return of([]);
        }
        return this.customerDataService?.getMulti(ids) || of([]);
      }),
      catchError((_) => {
        // in the event of an account group error, just return an empty list
        // sometimes the user doesn't have permission to access the account group
        return of([]);
      }),
    );

    return combineLatest([disputesResp$, customerData$]).pipe(
      map(([resp, customerData]) => {
        const customers: { [customerId: string]: CustomerData } = {};
        customerData.forEach((c) => {
          if (!c) {
            return;
          }
          customers[c.customerId] = c;
        });

        resp.data.forEach((d) => {
          const customer = customers[d.customerId];
          d.customerName = customer?.companyName || '';
        });

        return resp;
      }),
    );
  }

  listObjectFilters(_: string): Observable<GalaxyFilterDefinitionInterface[]> {
    return this.translateService.stream('DISPUTES.TABLE.COLUMN_HEADERS.STATUS').pipe(
      map((statusHeader) => {
        return [
          {
            fieldId: 'status',
            type: GalaxyFilterType.FILTER_TYPE_CHECKBOX,
            supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_IS],
            fieldName: statusHeader,
          },
        ];
      }),
    );
  }

  noFiltersApplied(): boolean {
    return this.disputesFilter().status < 1;
  }

  getFieldOptions?(fieldId: string, _: string): Observable<string[]> {
    if (fieldId === 'status') {
      return of([
        DisputeStatus.WON,
        DisputeStatus.LOST,
        DisputeStatus.NEEDS_RESPONSE,
        DisputeStatus.UNDER_REVIEW,
        DisputeStatus.WARNING_CLOSED,
        DisputeStatus.WARNING_NEEDS_RESPONSE,
        DisputeStatus.WARNING_UNDER_REVIEW,
      ]);
    }
    return of([]);
  }
}
